import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store } from '@ngxs/store';
import { CertificateService } from 'app/library/certificate/services/certificate.service';
import { CertificateRecordStatus } from 'app/library/certificate-record/models/certificate-record-status.model';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { CertificationFlatNode } from 'app/library/certification-provider/components/providers-all-list/providers-all-list.component';
import { CertificationProviderService } from 'app/library/certification-provider/services/certification-provider.service';
import { AddCertificateUserProfileDialogComponent } from 'app/library/certifications/components/dialogs/add-certificate-user-profile-dialog/add-certificate-user-profile-dialog.component';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { Module } from 'app/library/module/models/module.model';
import { ModuleCertificateService } from 'app/library/module-certificate/services/module-certificate.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CertificateRecord } from '../../models/certificate-record';
import { UserCourseCertificatesDataSource } from './datasource';

@Component({
    selector: 'app-user-course-certificates-list',
    templateUrl: './user-course-certificates-list.component.html',
    styleUrls: ['./user-course-certificates-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class UserCourseCertificatesListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    userCourseCertificates$: Observable<CertificateRecord[]>;

    @Input()
    userCourseCertificatesTotalCount$: Observable<number>;

    @Input()
    userId: string;

    @Input()
    canNavigateToModule: boolean = true;

    @Input()
    canUserManageCertificates: boolean = false;

    @Input()
    canDownloadCertificates: boolean = true;

    @Output()
    navigatedToModule = new EventEmitter<boolean>();

    dataSource: UserCourseCertificatesDataSource;
    displayedColumns = ['provider', 'name', 'course', 'issueDate', 'expirationDate', 'status', 'buttons'];
    isLoading: boolean = false;
    searchInput = new FormControl();
    isMyTeamsPage: boolean = false;
    certificatesStatuses = CertificateRecordStatus.all;
    profile = this._store.selectSnapshot(UserState.getMyInfo);
    sortActive = 'issueDate';
    sortDirection: 'asc' | 'desc' = 'desc';

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    @ViewChild(MatSort)
    sort: MatSort;

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _moduleManagersService: ModuleManagersService,
        private _certificateService: CertificateService,
        private _certificateRecordService: CertificateRecordService,
        private _certificationProviderService: CertificationProviderService,
        private _moduleCertificateService: ModuleCertificateService,
        private _translateService: TranslateService,
        private _matDialog: MatDialog,
        private _router: Router
    ) {
        super();

        this.isMyTeamsPage = this._router.url.includes('my-teams');
    }

    get canShowAddCertificate(): boolean {
        if (!this.canUserManageCertificates) {
            return false;
        }

        if (this.profile?.id?.toString() === this.userId) {
            return true;
        }

        return (
            this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT) ||
            (this.profile.permissions.includes(AdminPermissions.CERTIFICATION_WRITE) &&
                (this.profile.permissions.includes(AdminPermissions.USERS_READ) || this.profile.permissions.includes(AdminPermissions.USERS_WRITE)))
        );
    }

    ngOnInit(): void {
        this.dataSource = new UserCourseCertificatesDataSource(this.userCourseCertificates$, this._store, this._action$);

        this.dataSource.loading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isLoading) => {
            this.isLoading = isLoading;
        });
    }

    ngAfterViewInit(): void {
        this.loadCertificatesPage();

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadCertificatesPage(false);
        });

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadCertificatesPage(true);
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadCertificatesPage(true);
        });
    }

    downloadCertificate(certificate: CertificateRecord): void {
        if (!this.canDownloadCertificates) {
            return;
        }

        this._certificateService.downloadFinalGradeCertificate(certificate).then(() => {});
    }

    goToModule(module: Module): void {
        this._moduleManagersService.goToModule(this.userId, module, 'overview');
        this.navigatedToModule.emit(true);
    }

    async openCertificate(certificate: CertificateRecord): Promise<void> {
        this._router.navigate([`${window.location.pathname}`, { outlets: { modal: ['certificate', certificate.id] } }]);
    }

    async openAddReplaceCertificateDefaultDialog(certificate: CertificateRecord): Promise<void> {
        await this._certificateRecordService.fetchCertificateRecord(certificate.id);
        const certificateRecord = this._certificateRecordService.getCertificateRecord();
        const officialMetadata = certificateRecord?.officialCertificateMetadata;

        if (!officialMetadata?.certification) {
            return;
        }

        const dialogRef = this._matDialog.open(AddCertificateUserProfileDialogComponent, {
            width: '1100px',
            panelClass: 'full-screen-modal',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = 'PAGE.CERTIFICATE.BUTTON.ADD';
        dialogRef.componentInstance.userId = certificate.owner?.id || this.userId;
        dialogRef.componentInstance.providerId = officialMetadata.certification.provider.id;
        dialogRef.componentInstance.certificationId = officialMetadata.certification.id;
        dialogRef.componentInstance.certificate = this.buildFlatCertificate(certificate);
        dialogRef.componentInstance.minIssueDate = officialMetadata.issueDate;

        dialogRef.componentInstance.onReplaced.subscribe((result: boolean) => {
            if (result) {
                this.loadCertificatesPage(true);
            }
        });

        dialogRef.afterClosed().subscribe(async (newCertificate: CertificateRecord) => {
            if (newCertificate) {
                await this._certificationProviderService.replaceCertificate(certificate.id, newCertificate.id);
                this.loadCertificatesPage(true);
            }
        });
    }

    removeCertificate(certificate: CertificateRecord): void {
        const confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';
        confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('PAGE.CERTIFICATIONS.MESSAGE.DELETE_CERTIFICATE', {
            userName: certificate.owner?.fullName,
            certificateName: certificate.name,
        });

        confirmDialogRef.afterClosed().subscribe(async (result: boolean) => {
            if (result) {
                await this._moduleCertificateService.deleteCertificate(certificate.id);
                this.loadCertificatesPage(true);
            }
        });
    }

    canReplaceCertificate(certificate: CertificateRecord): boolean {
        return !!certificate?.officialCertificateMetadata?.certification;
    }

    isModuleCertificate(certificate: CertificateRecord): boolean {
        return !!certificate?.moduleUser?.id;
    }

    hasRowActions(certificate: CertificateRecord): boolean {
        if (!this.canUserManageCertificates && !this.canDownloadCertificates) {
            return false;
        }

        if (certificate?.filename && this.canDownloadCertificates) {
            return true;
        }

        if (!this.canUserManageCertificates) {
            return false;
        }

        return !this.isModuleCertificate(certificate) || this.canReplaceCertificate(certificate);
    }

    private buildFlatCertificate(certificate: CertificateRecord): CertificationFlatNode {
        const metadata = certificate.officialCertificateMetadata;
        const statusEntry = metadata ? this.certificatesStatuses[metadata.status] : null;

        return {
            id: certificate.id,
            expandable: false,
            provider: '',
            certificationName: '',
            avatarUrl: certificate.owner?.avatar?.src || '',
            name: certificate.owner?.fullName || '',
            certificateFullName: certificate.name,
            status: statusEntry?.translationLabel || null,
            statusValue: statusEntry?.value ?? null,
            issueDate: metadata?.issueDate || null,
            expirationDate: metadata?.expirationDate || null,
            level: 2,
            isModuleCertificate: this.isModuleCertificate(certificate),
            filename: certificate.filename,
            isCertificationTargetMet: null,
            certificatesCount: null,
            targetCount: null,
            ownerId: certificate.owner?.id || null,
        };
    }

    openAddCertificateDialog(): void {
        const dialogRef = this._matDialog.open(AddCertificateUserProfileDialogComponent, {
            width: '1100px',
            panelClass: 'full-screen-modal',
        });

        dialogRef.componentInstance.title = 'PAGE.CERTIFICATE.BUTTON.ADD';
        dialogRef.componentInstance.userId = this.userId;

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.loadCertificatesPage(true);
            }
        });
    }

    getStatusLabel(certificate: CertificateRecord): string {
        if (!certificate?.officialCertificateMetadata) {
            return null;
        }

        const status = this.certificatesStatuses[certificate.officialCertificateMetadata.status];

        return status?.translationLabel || null;
    }

    async loadCertificatesPage(resetPageIndex = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        const sortBy = this.sort?.active || this.sortActive;
        const sortOrder = this.sort?.direction || this.sortDirection;

        this.dataSource.loadCertificates(this.paginator.pageSize, this.paginator.pageIndex + 1, this.searchInput.value, this.userId, sortBy, sortOrder);
    }
}
